import React, { useMemo, useState } from 'react';

// Whitelist of known real tech brands. Scans every word in the product name.
const KNOWN_BRANDS = new Set([
  'Apple','Samsung','LG','Sony','Hisense','TCL','HP','Dell','Lenovo','MSI',
  'Asus','ASUS','Acer','Microsoft','Canon','Nikon','Fujifilm','GoPro','DJI',
  'JBL','Bose','Sennheiser','Harman','Beats','Marshall','Anker',
  'Logitech','Corsair','HyperX','Razer','SteelSeries','Kingston','WD',
  'Seagate','SanDisk','Crucial','Netgear','Ubiquiti','Cisco','Omada',
  'Zebra','Lightwave','LightWave','EcoFlow','Kensington','YoloLiv',
  'OnePlus','Xiaomi','Vivo','Oppo','OPPO','Realme','Infinix','Tecno',
  'Nokia','Motorola','Itel','Honor','Huawei','Meizu','Cursor',
  'Nintendo','Xbox','PlayStation','Oculus','Meta',
  'Epson','Brother','Xerox','Ricoh','Kodak',
  'Philips','Panasonic','Toshiba','Sharp','Hitachi',
  'Wacom','Elgato','Focusrite','APC','CyberPower','Eaton',
  'TP-Link','Belkin','Targus','Satechi','Ugreen',
]);

// Keyword-to-brand mapping for products sold without the brand name prefix
const BRAND_KEYWORDS = {
  Apple: ['iphone', 'macbook', 'ipad', 'airpods', 'imac', 'mac mini', 'mac pro', 'apple watch', 'apple tv', 'ipod'],
  Samsung: ['galaxy s', 'galaxy a', 'galaxy m', 'galaxy z', 'galaxy tab', 'galaxy watch', 'galaxy buds'],
  Sony: ['playstation', 'xperia', 'bravia', 'walkman'],
  Microsoft: ['surface pro', 'surface laptop', 'surface go', 'xbox'],
};

export function extractBrand(name) {
  if (!name) return 'Other';
  const lower = name.toLowerCase();
  // Check keyword map first (handles products sold without brand prefix)
  for (const [brand, keywords] of Object.entries(BRAND_KEYWORDS)) {
    if (keywords.some(kw => lower.includes(kw))) return brand;
  }
  // Then check word-by-word against the known brands whitelist
  const words = name.split(/\s+/);
  for (const word of words) {
    const clean = word.replace(/[^a-zA-Z-]/g, '');
    if (!clean) continue;
    if (KNOWN_BRANDS.has(clean)) return clean;
    const match = [...KNOWN_BRANDS].find(b => b.toLowerCase() === clean.toLowerCase());
    if (match) return match;
  }
  return 'Other';
}

export const DEFAULT_FILTERS = { brands: [], categories: [], minPrice: '', maxPrice: '' };

export function hasActiveFilters(filters) {
  return (
    (filters.brands && filters.brands.length > 0) ||
    (filters.categories && filters.categories.length > 0) ||
    filters.minPrice !== '' ||
    filters.maxPrice !== ''
  );
}

export function applyFilters(products, filters) {
  let result = products;
  if (filters.brands && filters.brands.length > 0) {
    result = result.filter(p => filters.brands.includes(extractBrand(p.name)));
  }
  if (filters.categories && filters.categories.length > 0) {
    result = result.filter(p => {
      const catName = p.category_name || (p.category && p.category.name) || '';
      return filters.categories.includes(catName);
    });
  }
  if (filters.minPrice !== '' && filters.minPrice !== undefined) {
    result = result.filter(p => parseFloat(p.price) >= parseFloat(filters.minPrice));
  }
  if (filters.maxPrice !== '' && filters.maxPrice !== undefined) {
    result = result.filter(p => parseFloat(p.price) <= parseFloat(filters.maxPrice));
  }
  return result;
}

const ChevronIcon = ({ open }) => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: 'transform 0.22s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function AccordionSection({ title, activeCount, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="fp-section">
      <button className="fp-accordion-header" onClick={() => setOpen(o => !o)}>
        <span className="fp-section-title">{title}</span>
        <div className="fp-accordion-right">
          {activeCount > 0 && <span className="fp-section-badge">{activeCount}</span>}
          <ChevronIcon open={open} />
        </div>
      </button>
      {open && <div className="fp-accordion-body">{children}</div>}
    </div>
  );
}

// mode: 'home'     => Brand + Category accordion sections
// mode: 'category' => Brand + Price Range accordion sections
function FilterPanel({ mode = 'home', products, categories, filters, onFiltersChange }) {
  const brands = useMemo(() => {
    const set = new Set(
      (products || []).map(p => extractBrand(p.name)).filter(b => b && b !== 'Other')
    );
    return [...set].sort();
  }, [products]);

  const categoryNames = useMemo(() => {
    if (mode !== 'home') return [];
    return (categories || []).map(c => c.name).filter(Boolean).sort();
  }, [categories, mode]);

  const priceRange = useMemo(() => {
    const prices = (products || []).map(p => parseFloat(p.price)).filter(n => !isNaN(n) && n > 0);
    if (!prices.length) return { min: 0, max: 999999 };
    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) };
  }, [products]);

  const activeCount =
    (filters.brands?.length || 0) +
    (filters.categories?.length || 0) +
    (filters.minPrice !== '' ? 1 : 0) +
    (filters.maxPrice !== '' ? 1 : 0);

  const toggleBrand = (brand) => {
    const curr = filters.brands || [];
    onFiltersChange({
      ...filters,
      brands: curr.includes(brand) ? curr.filter(b => b !== brand) : [...curr, brand],
    });
  };

  const toggleCategory = (cat) => {
    const curr = filters.categories || [];
    onFiltersChange({
      ...filters,
      categories: curr.includes(cat) ? curr.filter(c => c !== cat) : [...curr, cat],
    });
  };

  return (
    <div className="fp-dropdown" onClick={e => e.stopPropagation()}>
      <div className="fp-header">
        <div className="fp-header-left">
          <span className="fp-title">Filters</span>
          {activeCount > 0 && <span className="fp-badge">{activeCount}</span>}
        </div>
        {activeCount > 0 && (
          <button className="fp-clear-btn" onClick={() => onFiltersChange(DEFAULT_FILTERS)}>
            Clear all
          </button>
        )}
      </div>

      <div className="fp-body">
        {/* Brand — always shown */}
        <AccordionSection title="Brand" activeCount={filters.brands?.length || 0}>
          <div className="fp-chips">
            {brands.length === 0
              ? <span className="fp-empty">No brands available</span>
              : brands.map(brand => (
                  <button
                    key={brand}
                    className={`fp-chip${(filters.brands || []).includes(brand) ? ' fp-chip--active' : ''}`}
                    onClick={() => toggleBrand(brand)}
                  >
                    {brand}
                  </button>
                ))
            }
          </div>
        </AccordionSection>

        {/* Category — home mode only */}
        {mode === 'home' && (
          <AccordionSection title="Category" activeCount={filters.categories?.length || 0}>
            <div className="fp-chips">
              {categoryNames.length === 0
                ? <span className="fp-empty">No categories available</span>
                : categoryNames.map(cat => (
                    <button
                      key={cat}
                      className={`fp-chip${(filters.categories || []).includes(cat) ? ' fp-chip--active' : ''}`}
                      onClick={() => toggleCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))
              }
            </div>
          </AccordionSection>
        )}

        {/* Price Range — category mode only */}
        {mode === 'category' && (
          <AccordionSection
            title="Price Range (KES)"
            activeCount={(filters.minPrice !== '' ? 1 : 0) + (filters.maxPrice !== '' ? 1 : 0)}
          >
            <div className="fp-price-row">
              <div className="fp-price-field">
                <label className="fp-price-label">Min</label>
                <input
                  type="number"
                  className="fp-price-input"
                  placeholder={priceRange.min.toLocaleString()}
                  value={filters.minPrice}
                  min={0}
                  onChange={e => onFiltersChange({ ...filters, minPrice: e.target.value })}
                />
              </div>
              <span className="fp-price-sep">—</span>
              <div className="fp-price-field">
                <label className="fp-price-label">Max</label>
                <input
                  type="number"
                  className="fp-price-input"
                  placeholder={priceRange.max.toLocaleString()}
                  value={filters.maxPrice}
                  min={0}
                  onChange={e => onFiltersChange({ ...filters, maxPrice: e.target.value })}
                />
              </div>
            </div>
          </AccordionSection>
        )}
      </div>
    </div>
  );
}

export default FilterPanel;
